import { FC, Fragment, useState } from 'react';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Table,
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { FormAlert, useForm } from '@/libs/antd/form';
import {
	addFRMOfficeObservationParetoReceivable,
	deleteFRMOfficeObservationParetoReceivable,
	updateFRMOfficeObservationParetoReceivable,
} from '@/libs/fleet-api/services/visit-reports';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import rule from '@/libs/antd/rules';
import { useMutation } from '@tanstack/react-query';
import { useFRMVisitReportPageContext } from './VisitReportOfficePage';
import { OfficeObservationParetoReceivable } from '@/libs/fleet-api/services/types';
import { formatCurrency } from '@/libs/antd/formater';
import { inputCurrency } from '@/libs/antd/input-number';
import { useModals } from '@/components/modals/ModalProvider';

type VisitReporParetoReceivableForm = {
	input?: OfficeObservationParetoReceivable<'payload'>;
	lists: OfficeObservationParetoReceivable<'payload'>[];
};

const FRMVisitReportParetoReceivable: FC = () => {
	const modalAPI = useModals();
	const { surveyInformation, officeObservation } =
		useFRMVisitReportPageContext();
	const { warehouse } = officeObservation ?? {};
	const { form, value, fieldName } = useForm<VisitReporParetoReceivableForm>();
	const [modal, setModal] = useState<
		{ type: 'add' } | { type: 'edit'; index: number } | null
	>(null);

	const addFRMOfficeObservationParetoReceivableMutation = useMutation({
		mutationFn: addFRMOfficeObservationParetoReceivable,
	});

	const updateFRMOfficeObservationParetoReceivableMutation = useMutation({
		mutationFn: updateFRMOfficeObservationParetoReceivable,
	});

	const deleteFRMOfficeObservationParetoReceivableMutation = useMutation({
		mutationFn: deleteFRMOfficeObservationParetoReceivable,
	});

	return (
		<Form.Provider>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={
					{
						lists: warehouse?.paretoReceivables ?? [],
					} as VisitReporParetoReceivableForm
				}
				onFinish={async ({ input, lists }) => {
					if (modal?.type === 'edit') {
						await updateFRMOfficeObservationParetoReceivableMutation.mutateAsync(
							{
								...input!,
								surveyId: surveyInformation!.id!,
							},
						);
						form.setFieldsValue({
							lists: lists.map((list, index) => {
								return index === modal.index ? input! : list;
							}),
						});
					} else {
						const res =
							await addFRMOfficeObservationParetoReceivableMutation.mutateAsync(
								{
									...input!,
									surveyId: surveyInformation!.id!,
								},
							);
						form.setFieldsValue({
							lists: [...lists, { ...input!, id: res.data.id }],
						});
					}
					setModal(null);
				}}
				style={{ marginBottom: 30 }}
			>
				<Form.Item
					name={fieldName(['input'])}
					noStyle
				/>
				<Form.Item
					name={fieldName(['lists'])}
					noStyle
				/>

				<Form.Item shouldUpdate>
					{({ getFieldsValue, setFieldValue }) => {
						const values = getFieldsValue() as VisitReporParetoReceivableForm;
						const dataSource =
							values?.lists?.map((list, key?) => {
								return { ...list, key };
							}) ?? [];

						return (
							<Fragment>
								<Row style={{ display: 'flex', justifyContent: 'end' }}>
									<Button
										style={{
											color: '#1557BC',
											borderRadius: 50,
											fontSize: 12,
											height: 24,
											borderColor: '#1557BC',
											fontWeight: 700,
											marginBottom: 20,
										}}
										loading={
											addFRMOfficeObservationParetoReceivableMutation.isPending ||
											updateFRMOfficeObservationParetoReceivableMutation.isPending
										}
										onClick={() => setModal({ type: 'add' })}
										icon={'+'}
									>
										Add Another
									</Button>
								</Row>
								<Table
									bordered
									pagination={false}
									dataSource={dataSource}
									columns={[
										{
											title: (
												<span
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													Action
												</span>
											),
											render: (_, record, index) => {
												return (
													<Row
														gutter={[40, 20]}
														style={{
															display: 'flex',
															flexWrap: 'nowrap',
															justifyContent: 'center',
															paddingLeft: 20,
															paddingRight: 20,
														}}
													>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	setFieldValue(fieldName(['input']), record);
																	setModal({
																		type: 'edit',
																		index,
																	});
																}}
															>
																<EditOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={async (e) => {
																	e.preventDefault();
																	const confirmDelete =
																		await modalAPI.modal.confirm({
																			title: 'delete this data?',
																		});

																	if (confirmDelete) {
																		await deleteFRMOfficeObservationParetoReceivableMutation.mutateAsync(
																			record.id!,
																		);
																		form.setFieldsValue({
																			lists: values.lists.filter(
																				(_, listIndex) => listIndex !== index,
																			),
																		});
																	}
																}}
															>
																<DeleteOutlined />
															</a>
														</div>
													</Row>
												);
											},
											width: 1,
										},
										{
											title: 'Nama Bouwheer',
											render: (_, record) => record.bouwheerName ?? '-',
										},
										{
											title: 'Nama Pekerjaan',
											render: (_, record) => record.jobName ?? '-',
										},
										{
											title: 'No. Telp Perusahaan',
											render: (_, record) => record.phoneNumber ?? '-',
										},
										{
											title: 'Nilai Piutang (Rp)',
											render: (_, record) =>
												formatCurrency(record.receivableValue) ?? '-',
										},
										{
											title: 'Umur Piutang Terlama (Hari)',
											render: (_, record) => record.oldestReceivableAge ?? '-',
										},
										{
											title: 'Kendala (Jika Ada)',
											render: (_, record) => record.constraint ?? '-',
										},
									]}
								/>
								<FormAlert
									alerts={
										!value?.lists?.length
											? ['List Piutang Pareto wajib diisi']
											: []
									}
								/>
								<FleetModal
									open={Boolean(modal)}
									width={940}
									closeIcon={false}
									keyboard={false}
									maskClosable={false}
									buttonPosition="end"
									afterClose={() => {
										form.resetFields([fieldName('input')]);
									}}
									onCancel={() => {
										setModal(null);
									}}
									onOk={() => {
										form.submit();
									}}
								>
									<FleetTitle>Add List Piutang Pareto</FleetTitle>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'id'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'surveyId'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'bouwheerName'])}
												label="Nama Bouwheer"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'jobName'])}
												label="Nama Pekerjaan"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'phoneNumber'])}
												label="No. Telpon Perusahaan"
												labelCol={{ span: 10 }}
												rules={[rule.required(), rule.phoneNumber()]}
											>
												<Input maxLength={13} />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'receivableValue'])}
												label="Nilai Piutang (Rp)"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<InputNumber {...inputCurrency} />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'oldestReceivableAge'])}
												label="Umur Piutang Terlama (Hari)"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<InputNumber />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'constraint'])}
												label="Kendala (Jika Ada)"
												labelCol={{ span: 10 }}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
								</FleetModal>
							</Fragment>
						);
					}}
				</Form.Item>
				<Divider />
			</Form>
		</Form.Provider>
	);
};

export default FRMVisitReportParetoReceivable;
